import {useState} from 'react'
import {Link, useParams} from 'react-router'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {getScenario, runScenario} from '../../api/scenarios.js'
import {Button} from '../../components/button/button.jsx'
import {ScenarioPill} from '../../components/scenario-pill/scenario-pill.jsx'
import {BottomSheet} from '../../components/bottom-sheet/bottom-sheet.jsx'
import {ConditionNode} from '../../components/condition-node/condition-node.jsx'
import styles from './scenario-detail.module.scss'

export function ScenarioDetail() {
    const {id} = useParams()
    const [confirmOpen, setConfirmOpen] = useState(false)
    const queryClient = useQueryClient()

    const {data: scenario, isLoading} = useQuery({
        queryKey: ['scenarios', id],
        queryFn: () => getScenario(id),
        staleTime: 30_000
    })

    const runMutation = useMutation({
        mutationFn: () => runScenario(id),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['scenarios', 'active']})
            setConfirmOpen(false)
        }
    })

    if (isLoading || !scenario)
        return <div className={styles.screen}><p className={styles.loading}>Loading…</p></div>

    return (
        <div className={styles.screen}>
            <header className={styles.header}>
                <Link to="/scenarios" className={styles.back} aria-label="Back to scenarios">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.4">
                        <path d="M15 6l-6 6 6 6"/>
                    </svg>
                </Link>
                <div className={styles.heading}>
                    <p className={styles.eyebrow}>{scenario.type}</p>
                    <h1 className={styles.title}>{scenario.name}</h1>
                    {scenario.description && <p className={styles.description}>{scenario.description}</p>}
                </div>
                <ScenarioPill active={!scenario.disabled} label={scenario.disabled ? 'Disabled' : 'Live'}/>
            </header>

            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Conditions</h2>
                <div className={styles.conditionTree}>
                    <ConditionNode node={scenario.settings?.conditions} readOnly depth={0}/>
                </div>
            </section>

            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Actions</h2>
                <ul className={styles.actionList}>
                    {(scenario.settings?.actions || []).map((action, i) => (
                        <li key={i} className={styles.actionItem}>
                            <span className={styles.actionKind}>{action.kind}</span>
                            <span className={styles.actionTarget}>{action.controlId || action.scenarioId || '—'}</span>
                            {action.command && <span className={styles.actionCommand}>{action.command}</span>}
                        </li>
                    ))}
                    {(!scenario.settings?.actions || scenario.settings.actions.length === 0) && (
                        <li className={styles.actionEmpty}>No actions configured.</li>
                    )}
                </ul>
            </section>

            <div className={styles.cta}>
                <Button
                    onClick={() => setConfirmOpen(true)}
                    disabled={scenario.disabled || runMutation.isPending}
                >
                    Run now
                </Button>
            </div>

            <BottomSheet open={confirmOpen} onClose={() => setConfirmOpen(false)} title="Override automation?">
                <p className={styles.sheetBody}>
                    Running this scenario manually will execute its actions immediately, regardless of
                    whether the conditions currently match.
                </p>
                <div className={styles.sheetCta}>
                    <Button variant="ghost" onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button
                        onClick={() => runMutation.mutate()}
                        loading={runMutation.isPending}
                    >
                        {runMutation.isPending ? 'Running…' : 'Run now'}
                    </Button>
                </div>
                {runMutation.error && (
                    <p className={styles.sheetError}>Couldn't run scenario. Try again.</p>
                )}
            </BottomSheet>
        </div>
    )
}
